// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// ── Scoped localStorage ──────────────────────────────────────────────────────
//
// Every localStorage key the app writes is prefixed with the signed-in user's
// id. Before this, ~10 categories of content -- lessons, conceptual budgets,
// exclusions, labor rates, presets, templates -- lived under machine-wide keys,
// so on a shared computer whoever signed in next read, and then overwrote, the
// previous person's data. A brand-new account came up already holding the
// admin's budgets. This is containment: it makes accounts on one machine
// invisible to each other. The real fix for org-level content is to move it
// to org-scoped tables with RLS, as org_cost_codes already is.
//
// Adopt-once: the first account to sign in after this ships inherits the
// machine's existing (bare-key) data, so nobody's work vanishes on upgrade.
// A marker records who adopted; every later account starts clean.
//
// Device preferences -- dark mode, cookie consent -- stay unscoped on purpose.
// They belong to the machine, not the person. Onboarding-seen and
// import-skipped are per person: a new estimator on a shared PC still gets
// the onboarding.

const ADOPTED_MARKER = "oe:adopted_by";

/** Keys that describe the device, not the user. Never prefixed. */
const UNSCOPED = new Set<string>([
  "oe_dark_mode",
  "oe_cookie_consent",
  ADOPTED_MARKER,
]);

let currentUserId: string | null = null;

/** Call once per render of the app root, before any child reads storage. */
export function setStorageScope(userId: string | null | undefined): void {
  currentUserId = userId || null;
}

export function getStorageScope(): string | null {
  return currentUserId;
}

function prefix(): string {
  return currentUserId ? `oe:u:${currentUserId}:` : "";
}

/** The physical key for a logical one under the current scope. */
export function scopedKey(base: string): string {
  if (UNSCOPED.has(base) || !currentUserId) return base;
  return prefix() + base;
}

function storage(): Storage | null {
  try { return typeof localStorage !== "undefined" ? localStorage : null; } catch { return null; }
}

/**
 * Whether this user may inherit the machine's bare-key data. True for the
 * first signed-in user to ask (and for them thereafter); false for everyone
 * else, so a second account on the machine starts empty.
 */
function mayAdopt(ls: Storage): boolean {
  if (!currentUserId) return false;
  const who = ls.getItem(ADOPTED_MARKER);
  if (who === null) { ls.setItem(ADOPTED_MARKER, currentUserId); return true; }
  return who === currentUserId;
}

/** Raw string read. Same signature as localStorage.getItem. */
export function sGetRaw(base: string): string | null {
  const ls = storage(); if (!ls) return null;
  try {
    const k = scopedKey(base);
    const v = ls.getItem(k);
    if (v !== null || k === base) return v;
    // Scoped miss: inherit the bare key once, for the adopting user only.
    const bare = ls.getItem(base);
    if (bare !== null && mayAdopt(ls)) { ls.setItem(k, bare); return bare; }
    return null;
  } catch { return null; }
}

/** Raw string write. Same signature as localStorage.setItem. */
export function sSetRaw(base: string, value: string): void {
  const ls = storage(); if (!ls) return;
  try { ls.setItem(scopedKey(base), value); } catch {}
}

/** Same signature as localStorage.removeItem. */
export function sRemove(base: string): void {
  const ls = storage(); if (!ls) return;
  try { ls.removeItem(scopedKey(base)); } catch {}
}

/** JSON read with fallback. */
export function sGet<T>(base: string, fallback: T): T {
  const v = sGetRaw(base);
  if (v === null) return fallback;
  try { return JSON.parse(v) as T; } catch { return fallback; }
}

/** JSON write. */
export function sSet(base: string, value: unknown): void {
  try { sSetRaw(base, JSON.stringify(value)); } catch {}
}

/**
 * Logical (un-prefixed) names of every key in the current scope. Replaces
 * the `for (i < localStorage.length) localStorage.key(i)` scans, which would
 * otherwise see every user's keys.
 */
export function sKeys(): string[] {
  const ls = storage(); if (!ls) return [];
  const p = prefix();
  const out: string[] = [];
  try {
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i); if (!k) continue;
      if (p) { if (k.startsWith(p)) out.push(k.slice(p.length)); }
      else if (!k.startsWith("oe:u:")) out.push(k);
    }
  } catch {}
  return out;
}
